import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Download, ArrowLeft, CheckCircle, FileText, Lock, Unlock, Upload, X, Shield, Zap, Key, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import apiClient from '../lib/api';
import { getSessionId } from '../lib/session';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get or create session ID
const LockUnlockPdfPage = () => {
  const { toast } = useToast();
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [operation, setOperation] = useState('lock'); // 'lock' or 'unlock'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('lock-unlock-pdf-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper function to get PDF page count
  const getPdfPageCount = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (error) {
      console.error('Error reading PDF:', error);
      return 0;
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: "Error",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "File size too large. Maximum 10MB allowed",
        variant: "destructive",
      });
      return;
    }

    setUploadingFile(true);
    
    try {
      // Check if PDF is password protected
      let isPasswordProtected = false;
      try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        await loadingTask.promise;
        // If we can open it without password, it's not protected
        isPasswordProtected = false;
      } catch (error) {
        console.log('PDF check error:', error);
        // If error mentions password or encrypted, it's protected
        const errorMsg = error.message?.toLowerCase() || '';
        if (errorMsg.includes('password') || errorMsg.includes('encrypted') || error.name === 'PasswordException') {
          isPasswordProtected = true;
          console.log('Detected password-protected PDF');
        } else {
          // If it's another error, throw it
          throw error;
        }
      }

      // Get actual page count if not password protected
      let pageCount = 0;
      if (!isPasswordProtected) {
        pageCount = await getPdfPageCount(file);
      }

      // Upload to backend
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await apiClient.post(
        `${BACKEND_URL}/api/pdf/upload?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const pdfData = {
        id: uploadResponse.data.id,
        file: file,
        name: file.name,
        size: uploadResponse.data.size,
        pages: uploadResponse.data.pages || pageCount,
        uploadDate: uploadResponse.data.upload_date,
        preview: URL.createObjectURL(file),
        isPasswordProtected: isPasswordProtected
      };

      setUploadedPdf(pdfData);
      setResult(null);
      setPassword('');
      setConfirmPassword('');
      
      // Auto-select operation based on password protection
      if (isPasswordProtected) {
        setOperation('unlock');
        toast({
          title: "Password Protected PDF Detected",
          description: "This PDF is password protected. Please enter the password to unlock it.",
        });
      } else {
        setOperation('lock');
        toast({
          title: "Success",
          description: "PDF uploaded successfully. You can add password protection.",
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload PDF",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemovePdf = () => {
    setUploadedPdf(null);
    setResult(null);
    setPassword('');
    setConfirmPassword('');
  };

  const handleProcess = async () => {
    if (!uploadedPdf) {
      toast({
        title: "Error",
        description: "Please upload a PDF",
        variant: "destructive",
      });
      return;
    }

    if (!password) {
      toast({
        title: "Error",
        description: "Please enter a password",
        variant: "destructive",
      });
      return;
    }

    if (operation === 'lock' && password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const endpoint = operation === 'lock' ? '/api/pdf/lock' : '/api/pdf/unlock';
      
      const response = await apiClient.post(
        `${BACKEND_URL}${endpoint}?session_id=${sessionId}`,
        {
          pdf_id: uploadedPdf.id,
          password: password
        }
      );
      
      setResult(response.data);
      
      toast({
        title: "Success",
        description: `PDF ${operation === 'lock' ? 'locked' : 'unlocked'} successfully`,
      });
    } catch (error) {
      console.error('Process error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || `Failed to ${operation} PDF`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    
    try {
      const response = await apiClient.get(
        `${BACKEND_URL}${result.download_url}?session_id=${sessionId}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Downloaded ${result.name}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to download file",
        variant: "destructive",
      });
    }
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('lock-unlock-pdf-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Lock or Unlock PDF — Secure or Remove PDF Passwords | AllSmallTooLS</title>
        <meta name="description" content="Protect your PDF with a password or unlock secured PDFs easily. 100% free, safe, and browser-based." />
        <meta name="keywords" content="lock pdf, unlock pdf, pdf password remover, secure pdf, protect pdf file" />
        <link rel="canonical" href="https://allsmalltools.com/lock-unlock-pdf" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to PDF lock/unlock anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your PDF lock/unlock tool for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <Lock className="h-8 w-8 text-yellow-500" />
              <span>Lock/Unlock PDF</span>
            </h1>
            <p className="text-gray-600">Protect your PDF with password or remove password protection</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload & Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Upload & Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* PDF Upload */}
            {!uploadedPdf ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  uploadingFile ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  dragActive 
                    ? 'border-yellow-500 bg-yellow-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={uploadingFile ? undefined : handleDrag}
                onDragLeave={uploadingFile ? undefined : handleDrag}
                onDragOver={uploadingFile ? undefined : handleDrag}
                onDrop={uploadingFile ? undefined : handleDrop}
                onClick={uploadingFile ? undefined : () => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileInput}
                  className="hidden"
                  disabled={uploadingFile}
                />
                
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                  dragActive ? 'bg-yellow-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`h-8 w-8 ${dragActive ? 'text-yellow-600' : 'text-gray-400'}`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {uploadingFile ? 'Reading PDF...' : 'Upload PDF'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {uploadingFile ? 'Please wait while we analyze your PDF' : 'Drag and drop your PDF file here, or click to browse'}
                </p>
                <p className="text-sm text-gray-500">
                  Maximum file size: 10MB • Supported format: PDF
                </p>
              </div>
            ) : (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-16 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{uploadedPdf.name}</p>
                      <p className="text-sm text-gray-500">
                        {uploadedPdf.pages} pages • {uploadedPdf.size}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemovePdf}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Operation Selection */}
            {uploadedPdf && (
              <div className="space-y-4">
                {uploadedPdf.isPasswordProtected && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <Lock className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm text-yellow-800 font-medium">
                        Password Protected PDF Detected
                      </span>
                    </div>
                    <p className="text-xs text-yellow-700 mt-1">
                      This PDF requires a password to be opened. Enter the password below to unlock it.
                    </p>
                  </div>
                )}
                
                <Label>Select Operation</Label>
                <RadioGroup value={operation} onValueChange={setOperation}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value="unlock" 
                      id="unlock"
                    />
                    <Label 
                      htmlFor="unlock" 
                      className="cursor-pointer"
                    >
                      <div className="flex items-center space-x-2">
                        <Unlock className="h-4 w-4 text-green-600" />
                        <span>Unlock PDF (Remove Password)</span>
                      </div>
                      {!uploadedPdf.isPasswordProtected && (
                        <p className="text-xs text-blue-600 mt-1">💡 This PDF is not password protected</p>
                      )}
                      {uploadedPdf.isPasswordProtected && (
                        <p className="text-xs text-green-600 mt-1">✓ Detected: Password protected PDF</p>
                      )}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value="lock" 
                      id="lock"
                    />
                    <Label 
                      htmlFor="lock" 
                      className="cursor-pointer"
                    >
                      <div className="flex items-center space-x-2">
                        <Lock className="h-4 w-4 text-yellow-600" />
                        <span>Lock PDF (Add Password)</span>
                      </div>
                      {uploadedPdf.isPasswordProtected && (
                        <p className="text-xs text-blue-600 mt-1">💡 This PDF is already password protected</p>
                      )}
                      {!uploadedPdf.isPasswordProtected && (
                        <p className="text-xs text-green-600 mt-1">✓ Detected: No password protection</p>
                      )}
                    </Label>
                  </div>
                </RadioGroup>

                {/* Password Input */}
                <div className="space-y-2">
                  <Label>
                    {operation === 'lock' ? 'New Password' : 'Current Password'}
                  </Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={operation === 'lock' ? 'Enter new password' : 'Enter current password'}
                  />
                </div>

                {/* Confirm Password (only for lock) */}
                {operation === 'lock' && (
                  <div className="space-y-2">
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                    />
                  </div>
                )}

                <Button 
                  onClick={handleProcess}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                >
                  {loading ? `Processing...` : operation === 'lock' ? 'Lock PDF' : 'Unlock PDF'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">
                      {operation === 'lock' ? 'PDF Locked Successfully!' : 'PDF Unlocked Successfully!'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {operation === 'lock' 
                      ? 'Your PDF is now password protected' 
                      : 'Password protection has been removed from your PDF'}
                  </p>
                </div>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{result.name}</p>
                        <p className="text-sm text-gray-500">
                          {result.pages} pages • {result.size}
                        </p>
                      </div>
                      <Button
                        onClick={handleDownload}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Lock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No operation performed yet</p>
                <p className="text-sm">Upload a PDF and choose an operation</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Why Use Our Lock/Unlock PDF Tool Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Lock & Unlock PDF Tool?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Password Protection</h3>
              <p className="text-gray-600">
                Lock your PDF files with strong password protection. Secure sensitive documents like contracts, reports, and personal files from unauthorized access.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Key className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Easy Unlock</h3>
              <p className="text-gray-600">
                Remove password protection from PDFs you own. Unlock PDFs quickly when you need to edit or share them without restrictions.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Fast & Secure</h3>
              <p className="text-gray-600">
                Lock or unlock PDFs in seconds with our secure online tool. All files are processed over encrypted connections and deleted after use.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Whether you need to protect confidential PDFs with a password or unlock PDFs for editing – our free tool makes it simple. Add password security to sensitive documents or remove restrictions from PDFs you own. Try our lock/unlock PDF tool today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/split-pdf" className="text-yellow-600 hover:text-yellow-800 text-sm font-medium">
                → Split PDF Files
              </Link>
              <Link to="/merge-pdf" className="text-yellow-600 hover:text-yellow-800 text-sm font-medium">
                → Merge PDF Files
              </Link>
              <Link to="/compress-pdf" className="text-yellow-600 hover:text-yellow-800 text-sm font-medium">
                → Compress PDF
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Lock/Unlock PDF Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Lock & Unlock PDF Files</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Upload Your PDF:</strong> Click the upload area or drag and drop your PDF file. Our tool supports password-protected and unprotected files.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Choose Operation:</strong> Select "Lock PDF" to add password protection, or "Unlock PDF" to remove existing password. Enter your password details.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>Process PDF:</strong> Click "Lock PDF" or "Unlock PDF" button. Our tool will securely process your file in seconds.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Download Protected/Unlocked PDF:</strong> Download your password-protected or unlocked PDF file. Your original file remains unchanged.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is it free to lock/unlock PDFs?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our lock/unlock PDF tool is completely free. Add password protection or remove PDF passwords without any fees, registration, or limitations.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I unlock any password-protected PDF?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">You can only unlock PDFs if you know the password. Our tool removes password protection from PDFs you own – it cannot crack or bypass unknown passwords for security reasons.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">How secure is password-protected PDF?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Password-protected PDFs use strong encryption. Choose a strong, unique password for maximum security. Your locked PDF will require the correct password to open or edit.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Are my PDFs safe when using this tool?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! All files are processed over secure, encrypted connections. We automatically delete uploaded and processed files immediately after the operation. Your PDFs and passwords remain completely private.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default LockUnlockPdfPage;
